import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CopyButton,
  Group,
  rem,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { graphql } from "gql.tada";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useSelectedOrganizationId } from "../../../../business/OrganizationId";
import { Player } from "../../../../gql/graphql";
import { DetailPageHeader } from "../../components/PageHeader";
import { StatCard } from "../../components/StatCard";
import { EmptyState } from "../../components/EmptyState";
import { DetailPageSkeleton } from "../../components/SkeletonLoader";
import { CustomFieldsWithEditableValue } from "../../components/form/CustomFieldsWithEditableValue";
import { openPlayerInventoryModal } from "../gamification/components/player/PlayerInventoryManager";
import { openCurrencyAdjustmentModal } from "../gamification/components/player/CurrencyAdjustmentModal";
import { ItemCardCompact } from "../gamification/components/ItemCard";
import {
  IconUser,
  IconId,
  IconLink,
  IconCopy,
  IconCheck,
  IconPackage,
  IconCoins,
  IconChartBar,
  IconShoppingCart,
  IconDeviceGamepad2,
  IconCalendar,
  IconHash,
  IconAdjustments,
  IconBrandDiscord,
  IconBrandSteam,
  IconWorld,
  IconDatabase,
  IconTag,
  IconTrash,
} from "@tabler/icons-react";
import { CustomFieldValuesPanel } from "../data/values/CustomFieldValuesPanel";
import { ObjectMetadataPanel } from "../data/metadata/ObjectMetadataPanel";

// --- GraphQL ---

export const OrganizationQuery = graphql(`
  query organizationGet($id: ID!) {
    organization(id: $id) {
      id
      name
      configuration {
        customFields {
          name
          type
          order
          property
          unique
          required
          editability
          visibility
        }
      }
    }
  }
`);

const UpdatePlayerMutation = graphql(`
  mutation updatePlayer($id: ID!, $input: UpdatePlayerInput!) {
    updatePlayer(id: $id, input: $input) {
      id
    }
  }
`);

const DeleteAccountIdentityMutation = gql`
  mutation deleteAccountIdentity($identityProviderId: ID!, $accountId: ID) {
    deleteAccountIdentity(
      identityProviderId: $identityProviderId
      accountId: $accountId
    )
  }
`;

const PlayerSkillRatingsQuery = graphql(`
  query playerSkillRatings($playerId: ID!) {
    playerSkillRatings(playerId: $playerId) {
      ratingId
      value
    }

    organizationSkillRatings {
      id
      name
    }
  }
`);

const PLAYER_CURRENCIES_QUERY = gql`
  query PlayerCurrenciesForProfile($playerId: ID!) {
    playerCurrencies(
      filter: { playerProfileIds: [$playerId] }
      page: { first: 100 }
    ) {
      nodes {
        id
        balance
        currencyId
      }
    }
  }
`;

const CURRENCIES_QUERY = gql`
  query CurrenciesForProfile {
    currencies(page: { first: 100 }) {
      nodes {
        id
        name
        symbol
        description
      }
    }
  }
`;

const PLAYER_ITEMS_QUERY = gql`
  query PlayerItemsForProfile($playerId: ID!) {
    playerItems(
      filter: { playerProfileIds: [$playerId] }
      page: { first: 100 }
    ) {
      nodes {
        id
        itemId
        quantity
        metadata {
          property
          value
        }
      }
    }
  }
`;

const ITEMS_QUERY = gql`
  query ItemsForProfile {
    items(page: { first: 500 }) {
      nodes {
        id
        name
        description
        categoryId
        metadata {
          property
          value
        }
      }
    }
  }
`;

const ITEM_CATEGORIES_QUERY = gql`
  query ItemCategoriesForProfile {
    itemCategories(page: { first: 100 }) {
      nodes {
        id
        name
      }
    }
  }
`;

const PLAYER_PURCHASES_QUERY = gql`
  query PlayerPurchasesForProfile($playerId: ID!) {
    playerShopProductPurshases(
      filter: { playerProfileIds: [$playerId] }
      page: { first: 50 }
    ) {
      nodes {
        id
        shopProductId
        quantity
        createdAt
      }
      totalCount
    }
  }
`;

const PLAYER_MATCHES_QUERY = gql`
  query PlayerMatchesForProfile($playerId: ID!, $cursor: String) {
    playerMatchesHistory(
      playerId: $playerId
      page: { first: 20, after: $cursor }
    ) {
      nodes {
        id
        order
        status
        createdAt
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

// --- Styles ---

const useStyles = createStyles((theme) => ({
  profileCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  sectionCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  label: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.colors.dark[2],
  },
  value: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    color: theme.colors.dark[0],
  },
  missing: {
    fontSize: theme.fontSizes.sm,
    fontStyle: "italic",
    color: theme.colors.dark[3],
  },
  identityCard: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[5]}`,
    transition: "border-color 200ms ease",
    "&:hover": {
      borderColor: theme.colors.dark[4],
    },
  },
  identityProviderName: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    color: theme.colors.dark[0],
  },
  identityPropertyKey: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
    fontWeight: 500,
  },
  identityPropertyValue: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[1],
    wordBreak: "break-all",
  },
  ratingCard: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[5]}`,
    textAlign: "center",
    transition: "border-color 200ms ease",
    "&:hover": {
      borderColor: theme.colors.cyan[7],
    },
  },
  ratingValue: {
    fontSize: rem(28),
    fontWeight: 700,
    color: theme.colors.cyan[4],
    lineHeight: 1,
  },
  ratingName: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    color: theme.colors.dark[1],
    marginTop: theme.spacing.xs,
  },
  ratingNoValue: {
    fontSize: rem(28),
    fontWeight: 700,
    color: theme.colors.dark[4],
    lineHeight: 1,
  },
  currencyRow: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[5]}`,
    transition: "border-color 200ms ease",
    "&:hover": {
      borderColor: theme.colors.amber[7],
    },
  },
  currencyBalance: {
    fontSize: rem(22),
    fontWeight: 700,
    color: theme.colors.amber[4],
    lineHeight: 1,
  },
  currencySymbol: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    color: theme.colors.dark[3],
  },
  purchaseRow: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  matchRow: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  tabsList: {
    borderBottom: `1px solid ${theme.colors.dark[5]}`,
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xs,
  },
}));

// --- Helpers ---

const getIdentityIcon = (providerName: string | undefined | null) => {
  if (!providerName) return <IconWorld size={18} />;
  const lower = providerName.toLowerCase();
  if (lower.includes("discord")) return <IconBrandDiscord size={18} />;
  if (lower.includes("steam")) return <IconBrandSteam size={18} />;
  return <IconLink size={18} />;
};

const formatDate = (date: string | null | undefined) => {
  if (!date) return null;
  return new Date(date).toLocaleString();
};

type ItemRarity = "common" | "uncommon" | "rare" | "epic" | "legendary";

const getRarityFromMetadata = (
  metadata: Array<{ property: string; value: string }> | undefined,
): ItemRarity => {
  const rarityMeta = metadata?.find(
    (m) => m.property.toLowerCase() === "rarity",
  );
  if (rarityMeta) {
    const value = rarityMeta.value.toLowerCase();
    if (["common", "uncommon", "rare", "epic", "legendary"].includes(value)) {
      return value as ItemRarity;
    }
  }
  return "common";
};

// --- Component ---

export const PlayerView = () => {
  const { classes } = useStyles();
  const playerId = useParams().playerId as string;
  const organizationId = useSelectedOrganizationId();
  const [editLoading, setEditLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>("overview");

  // Organization config (for custom fields)
  const { data: organization, loading: orgLoading } = useQuery<any>(
    OrganizationQuery,
    { variables: { id: organizationId ?? "" } },
  );

  // Player data
  const {
    data,
    loading,
    error: playerError,
    refetch: refetchPlayer,
  } = useQuery<{ player: Player }>(
    gql`
      query ($id: ID!, $properties: [String!]!) {
        player(id: $id) {
          id
          username
          ownerId
          organizationCustomId
          createdAt
          updatedAt
          customFields(properties: $properties) {
            property
            value
          }
          identities {
            organizationIdentityProviderId
            isCreationIdentity
            organizationIdentityProvider {
              name
            }
            properties {
              property
              value
            }
          }
        }
      }
    `,
    {
      variables: {
        id: playerId,
        properties:
          organization?.organization?.configuration?.customFields?.map(
            (f: any) => f.property,
          ) ?? [],
      },
      skip: orgLoading,
    },
  );

  // Skill ratings
  const { data: skillRatingsData } = useQuery<any>(PlayerSkillRatingsQuery, {
    variables: { playerId },
  });

  // Currencies - always load (for stat cards + currencies tab)
  const { data: playerCurrenciesData, refetch: refetchCurrencies } =
    useQuery<any>(PLAYER_CURRENCIES_QUERY, {
      variables: { playerId },
    });
  const { data: currenciesData } = useQuery<any>(CURRENCIES_QUERY);

  // Items / Inventory - always load player items count, lazy load catalog
  const { data: playerItemsData, refetch: refetchItems } = useQuery<any>(
    PLAYER_ITEMS_QUERY,
    {
      variables: { playerId },
    },
  );
  const { data: itemsData } = useQuery<any>(ITEMS_QUERY, {
    skip: activeTab !== "inventory",
  });
  const { data: categoriesData } = useQuery<any>(ITEM_CATEGORIES_QUERY, {
    skip: activeTab !== "inventory",
  });

  // Purchases - lazy load
  const { data: purchasesData } = useQuery<any>(PLAYER_PURCHASES_QUERY, {
    variables: { playerId },
    skip: activeTab !== "purchases",
  });

  // Matches - lazy load
  const { data: matchesData } = useQuery<any>(PLAYER_MATCHES_QUERY, {
    variables: { playerId },
    skip: activeTab !== "matches",
  });

  const [updatePlayer] = useMutation<any>(UpdatePlayerMutation);
  const [deleteAccountIdentity] = useMutation<{
    deleteAccountIdentity: boolean;
  }>(DeleteAccountIdentityMutation);

  if (loading || orgLoading) {
    return <DetailPageSkeleton />;
  }

  if (playerError) {
    return (
      <EmptyState
        title="Failed to load player"
        description={playerError.message}
        iconType="error"
        actionLabel="Retry"
        onAction={() => refetchPlayer()}
      />
    );
  }

  const player = data?.player;
  const customFields =
    organization?.organization?.configuration?.customFields ?? [];

  // Currencies mapping
  const currencies = currenciesData?.currencies?.nodes ?? [];
  const playerCurrencies = playerCurrenciesData?.playerCurrencies?.nodes ?? [];

  // Items mapping
  const items = itemsData?.items?.nodes ?? [];
  const categories = categoriesData?.itemCategories?.nodes ?? [];
  const playerItems = playerItemsData?.playerItems?.nodes ?? [];

  // Purchases
  const purchases = purchasesData?.playerShopProductPurshases?.nodes ?? [];
  const purchasesCount =
    purchasesData?.playerShopProductPurshases?.totalCount ?? 0;

  // Matches
  const matches = matchesData?.playerMatchesHistory?.nodes ?? [];
  const matchesCount = matchesData?.playerMatchesHistory?.totalCount ?? 0;

  // Skill ratings
  const orgSkillRatings = skillRatingsData?.organizationSkillRatings ?? [];
  const playerSkillRatings = skillRatingsData?.playerSkillRatings ?? [];

  // Counts for stats
  const identitiesCount = player?.identities?.length ?? 0;
  const itemsTotalQuantity = playerItems.reduce(
    (sum: number, pi: any) => sum + (pi.quantity ?? 0),
    0,
  );
  const uniqueItemsCount = playerItems.length;

  const isLinked = !!player?.ownerId;

  return (
    <div className="wp-fade-in">
      <DetailPageHeader
        title={player?.username ?? "Player"}
        subtitle={`Player ID: ${playerId}`}
        status={isLinked ? "verified" : "unverified"}
        statusColor={isLinked ? "green" : "amber"}
        onRefresh={() => {
          refetchPlayer();
          refetchCurrencies();
          refetchItems();
        }}
        showBreadcrumbs={false}
        actions={[
          {
            label: "Manage Inventory",
            icon: <IconPackage size={16} />,
            onClick: () =>
              openPlayerInventoryModal({
                playerId,
                playerName: player?.username ?? "Player",
                onSuccess: () => refetchItems(),
              }),
            variant: "light",
          },
          {
            label: "Adjust Currency",
            icon: <IconCoins size={16} />,
            onClick: () =>
              openCurrencyAdjustmentModal({
                playerId,
                playerName: player?.username ?? "Player",
                onSuccess: () => refetchCurrencies(),
              }),
            variant: "light",
          },
        ]}
      />

      {/* Stat Cards */}
      <SimpleGrid
        cols={{ base: 2, md: 4 }}
        className="wp-stagger-children"
        mb="lg"
      >
        <StatCard
          label="Identities"
          value={identitiesCount}
          iconType="players"
          description={isLinked ? "Account linked" : "Anonymous player"}
        />
        <StatCard
          label="Skill Ratings"
          value={playerSkillRatings.length}
          iconType="stats"
          description={`${orgSkillRatings.length} configured`}
        />
        <StatCard
          label="Inventory"
          value={uniqueItemsCount}
          iconType="games"
          description={`${itemsTotalQuantity} total items`}
          onClick={() => setActiveTab("inventory")}
          tooltip="View inventory"
        />
        <StatCard
          label="Currencies"
          value={playerCurrencies.length}
          iconType="currency"
          description={`${currencies.length} available`}
          onClick={() => setActiveTab("currencies")}
          tooltip="View currencies"
        />
      </SimpleGrid>

      {/* Tabbed Content */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List className={classes.tabsList} mb="lg">
          <Tabs.Tab value="overview" leftSection={<IconUser size={14} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="inventory" leftSection={<IconPackage size={14} />}>
            Inventory ({uniqueItemsCount})
          </Tabs.Tab>
          <Tabs.Tab value="currencies" leftSection={<IconCoins size={14} />}>
            Currencies ({playerCurrencies.length})
          </Tabs.Tab>
          <Tabs.Tab
            value="purchases"
            leftSection={<IconShoppingCart size={14} />}
          >
            Purchases ({purchasesCount})
          </Tabs.Tab>
          <Tabs.Tab
            value="matches"
            leftSection={<IconDeviceGamepad2 size={14} />}
          >
            Matches ({matchesCount})
          </Tabs.Tab>
          <Tabs.Tab
            value="custom-fields"
            leftSection={<IconDatabase size={14} />}
          >
            Custom Fields
          </Tabs.Tab>
          <Tabs.Tab value="metadata" leftSection={<IconTag size={14} />}>
            Metadata
          </Tabs.Tab>
        </Tabs.List>

        {/* Overview Tab */}
        <Tabs.Panel value="overview">
          <div className="wp-fade-in">
            <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
              {/* Player Info Card */}
              <Card className={classes.profileCard} p="md" radius="lg">
                <Group justify="space-between" mb="md">
                  <Group gap="sm">
                    <Avatar size="lg" radius="xl" color="cyan">
                      {player?.username?.charAt(0)?.toUpperCase() ?? "?"}
                    </Avatar>
                    <div>
                      <Text fw={600} size="lg">
                        {player?.username}
                      </Text>
                      <Badge
                        color={isLinked ? "green" : "amber"}
                        variant="light"
                        size="sm"
                      >
                        {isLinked ? "Linked Account" : "Anonymous"}
                      </Badge>
                    </div>
                  </Group>
                  <CopyButton value={playerId}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? "Copied!" : "Copy ID"}>
                        <Button
                          variant="subtle"
                          size="xs"
                          leftSection={
                            copied ? (
                              <IconCheck size={14} />
                            ) : (
                              <IconCopy size={14} />
                            )
                          }
                          onClick={copy}
                          color={copied ? "green" : "gray"}
                        >
                          {copied ? "Copied" : "Copy ID"}
                        </Button>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>

                <Stack gap="sm">
                  <div className={classes.infoRow}>
                    <IconId size={14} color="gray" />
                    <Text className={classes.label}>Player ID</Text>
                  </div>
                  <Text className={classes.value} mb="xs">
                    {playerId}
                  </Text>

                  {player?.organizationCustomId && (
                    <>
                      <div className={classes.infoRow}>
                        <IconHash size={14} color="gray" />
                        <Text className={classes.label}>Custom ID</Text>
                      </div>
                      <Text className={classes.value} mb="xs">
                        {player.organizationCustomId}
                      </Text>
                    </>
                  )}

                  <div className={classes.infoRow}>
                    <IconCalendar size={14} color="gray" />
                    <Text className={classes.label}>Created</Text>
                  </div>
                  <Text className={classes.value}>
                    {formatDate(player?.createdAt) ?? "Unknown"}
                  </Text>
                </Stack>
              </Card>

              {/* Skill Ratings Card */}
              <Card className={classes.profileCard} p="md" radius="lg">
                <Group justify="space-between" mb="md">
                  <Text fw={600}>Skill Ratings</Text>
                  <IconChartBar size={18} color="gray" />
                </Group>

                {orgSkillRatings.length === 0 ? (
                  <EmptyState
                    title="No skill ratings"
                    description="No skill ratings configured for this organization."
                    iconType="stats"
                    compact
                  />
                ) : (
                  <SimpleGrid cols={{ base: 1, sm: 2 }}>
                    {orgSkillRatings.map((rating: any) => {
                      const playerRating = playerSkillRatings.find(
                        (r: any) => r.ratingId === rating.id,
                      );
                      return (
                        <Card
                          key={rating.id}
                          className={classes.ratingCard}
                          p="sm"
                          radius="md"
                        >
                          <Text
                            className={
                              playerRating
                                ? classes.ratingValue
                                : classes.ratingNoValue
                            }
                          >
                            {playerRating
                              ? Math.round(playerRating.value)
                              : "---"}
                          </Text>
                          <Text className={classes.ratingName}>
                            {rating.name}
                          </Text>
                        </Card>
                      );
                    })}
                  </SimpleGrid>
                )}
              </Card>
            </SimpleGrid>

            {/* Custom Fields */}
            <Card className={classes.sectionCard} p="md" radius="lg" mb="lg">
              <Group justify="space-between" mb="md">
                <Text fw={600}>Custom Fields</Text>
                <IconAdjustments size={18} color="gray" />
              </Group>

              {!customFields.length ? (
                <EmptyState
                  title="No custom fields"
                  description="No custom fields configured for this organization."
                  compact
                />
              ) : (
                <SimpleGrid cols={2} spacing={0}>
                  <CustomFieldsWithEditableValue
                    fields={customFields}
                    loading={editLoading}
                    defaultValues={player?.customFields?.reduce(
                      (acc, field) => {
                        if (field.value) {
                          acc[field.property] = field.value;
                        }
                        return acc;
                      },
                      {} as Record<string, string>,
                    )}
                    onSubmit={async (property, value) => {
                      setEditLoading(true);
                      await updatePlayer({
                        variables: {
                          id: playerId,
                          input: {
                            customFields: [{ property, value }],
                          },
                        },
                      })
                        .then((data) => {
                          if (data?.error && data.error) {
                            throw new Error(data.error.message);
                          }
                          notifications.show({
                            title: "Updated",
                            message: "Custom field updated",
                            color: "green",
                            autoClose: 3000,
                          });
                          refetchPlayer();
                        })
                        .finally(() => {
                          setEditLoading(false);
                        });
                    }}
                  />
                </SimpleGrid>
              )}
            </Card>

            {/* Identities */}
            <Card className={classes.sectionCard} p="md" radius="lg">
              <Group justify="space-between" mb="md">
                <Text fw={600}>Linked Identities</Text>
                <IconLink size={18} color="gray" />
              </Group>

              {!identitiesCount ? (
                <EmptyState
                  title="No linked identities"
                  description="This player has no linked identity providers."
                  iconType="player"
                  compact
                />
              ) : (
                <Stack gap="sm">
                  {player?.identities?.map((identity, idx) => {
                    const handleDeleteIdentity = () => {
                      modals.openConfirmModal({
                        title: "Remove linked identity",
                        centered: true,
                        children: (
                          <Text size="sm">
                            Are you sure you want to remove the{" "}
                            <strong>
                              {identity.organizationIdentityProvider?.name ??
                                "Unknown Provider"}
                            </strong>{" "}
                            identity from this player? This action cannot be
                            undone.
                          </Text>
                        ),
                        labels: { confirm: "Remove", cancel: "Cancel" },
                        confirmProps: { color: "red" },
                        onConfirm: async () => {
                          try {
                            const result = await deleteAccountIdentity({
                              variables: {
                                identityProviderId:
                                  identity.organizationIdentityProviderId,
                                accountId: player?.ownerId,
                              },
                            });
                            if (result?.error)
                              throw new Error(result.error.message);
                            await refetchPlayer();
                            notifications.show({
                              title: "Identity removed",
                              message: "The linked identity has been removed.",
                              color: "green",
                              autoClose: 3000,
                            });
                          } catch (error) {
                            notifications.show({
                              title: "Remove failed",
                              message:
                                error instanceof Error &&
                                error.message.length < 200
                                  ? error.message
                                  : "Could not remove identity",
                              color: "red",
                              autoClose: 5000,
                            });
                          }
                        },
                      });
                    };

                    return (
                      <Card
                        key={idx}
                        className={classes.identityCard}
                        p="sm"
                        radius="md"
                      >
                        <Group gap="sm" mb="xs" justify="space-between">
                          <Group gap="sm">
                            <ThemeIcon
                              size="md"
                              radius="md"
                              variant="light"
                              color="cyan"
                            >
                              {getIdentityIcon(
                                identity.organizationIdentityProvider?.name,
                              )}
                            </ThemeIcon>
                            <Text className={classes.identityProviderName}>
                              {identity.organizationIdentityProvider?.name ??
                                "Unknown Provider"}
                            </Text>
                          </Group>
                          <Tooltip
                            label={
                              identity.isCreationIdentity
                                ? "Creation identity cannot be removed"
                                : "Remove identity"
                            }
                          >
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              size="sm"
                              disabled={identity.isCreationIdentity ?? false}
                              onClick={handleDeleteIdentity}
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                          {identity.properties.map(
                            (property, pIdx: number) => (
                              <Box key={pIdx}>
                                <Text className={classes.identityPropertyKey}>
                                  {property.property}
                                </Text>
                                {property.value.startsWith("http://") ||
                                property.value.startsWith("https://") ? (
                                  <Anchor
                                    target="_blank"
                                    href={property.value}
                                    size="sm"
                                  >
                                    {property.value}
                                  </Anchor>
                                ) : (
                                  <Text
                                    className={classes.identityPropertyValue}
                                  >
                                    {property.value}
                                  </Text>
                                )}
                              </Box>
                            ),
                          )}
                        </SimpleGrid>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </Card>
          </div>
        </Tabs.Panel>

        {/* Inventory Tab */}
        <Tabs.Panel value="inventory">
          <div className="wp-fade-in">
            <Group justify="space-between" mb="lg">
              <div>
                <Text fw={600} size="lg">
                  Player Inventory
                </Text>
                <Text size="sm" c="dimmed">
                  {uniqueItemsCount} unique items, {itemsTotalQuantity} total
                </Text>
              </div>
              <Button
                leftSection={<IconPackage size={16} />}
                variant="light"
                onClick={() =>
                  openPlayerInventoryModal({
                    playerId,
                    playerName: player?.username ?? "Player",
                    onSuccess: () => refetchItems(),
                  })
                }
                className="wp-btn-press"
              >
                Manage Inventory
              </Button>
            </Group>

            {playerItems.length === 0 ? (
              <EmptyState
                title="Empty inventory"
                description="This player has no items yet. Use the Manage Inventory button to add items."
                iconType="shop"
                actionLabel="Add Items"
                onAction={() =>
                  openPlayerInventoryModal({
                    playerId,
                    playerName: player?.username ?? "Player",
                    onSuccess: () => refetchItems(),
                  })
                }
              />
            ) : (
              <Stack gap="xs" className="wp-stagger-children">
                {playerItems.map((pi: any) => {
                  const item = items.find((i: any) => i.id === pi.itemId);
                  if (!item) return null;
                  const category = categories.find(
                    (c: any) => c.id === item.categoryId,
                  );
                  const rarity = getRarityFromMetadata(item.metadata);

                  return (
                    <ItemCardCompact
                      key={pi.id}
                      id={item.id}
                      name={item.name}
                      description={
                        category
                          ? `${category.name} - ${item.description ?? ""}`
                          : item.description
                      }
                      quantity={pi.quantity}
                      rarity={rarity}
                    />
                  );
                })}
              </Stack>
            )}
          </div>
        </Tabs.Panel>

        {/* Currencies Tab */}
        <Tabs.Panel value="currencies">
          <div className="wp-fade-in">
            <Group justify="space-between" mb="lg">
              <div>
                <Text fw={600} size="lg">
                  Currency Balances
                </Text>
                <Text size="sm" c="dimmed">
                  {playerCurrencies.length} currencies with balance
                </Text>
              </div>
              <Button
                leftSection={<IconCoins size={16} />}
                variant="light"
                onClick={() =>
                  openCurrencyAdjustmentModal({
                    playerId,
                    playerName: player?.username ?? "Player",
                    onSuccess: () => refetchCurrencies(),
                  })
                }
                className="wp-btn-press"
              >
                Adjust Currency
              </Button>
            </Group>

            {currencies.length === 0 ? (
              <EmptyState
                title="No currencies"
                description="No currencies are configured for this organization."
                iconType="default"
              />
            ) : (
              <Stack gap="xs" className="wp-stagger-children">
                {currencies.map((currency: any) => {
                  const playerCurrency = playerCurrencies.find(
                    (pc: any) => pc.currencyId === currency.id,
                  );
                  const balance = playerCurrency?.balance ?? 0;

                  return (
                    <Card
                      key={currency.id}
                      className={classes.currencyRow}
                      p="md"
                      radius="md"
                    >
                      <Group justify="space-between">
                        <Group gap="sm">
                          <ThemeIcon
                            size="lg"
                            radius="md"
                            variant="light"
                            color="amber"
                          >
                            <IconCoins size={18} />
                          </ThemeIcon>
                          <div>
                            <Text fw={600} size="sm">
                              {currency.name}
                            </Text>
                            {currency.description && (
                              <Text size="xs" color="dimmed" lineClamp={1}>
                                {currency.description}
                              </Text>
                            )}
                          </div>
                        </Group>
                        <Group gap="xs" align="baseline">
                          <Text className={classes.currencyBalance}>
                            {balance.toLocaleString()}
                          </Text>
                          <Text className={classes.currencySymbol}>
                            {currency.symbol}
                          </Text>
                        </Group>
                      </Group>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </div>
        </Tabs.Panel>

        {/* Purchases Tab */}
        <Tabs.Panel value="purchases">
          <div className="wp-fade-in">
            <Group justify="space-between" mb="lg">
              <div>
                <Text fw={600} size="lg">
                  Purchase History
                </Text>
                <Text size="sm" c="dimmed">
                  {purchasesCount} total purchases
                </Text>
              </div>
            </Group>

            {purchases.length === 0 ? (
              <EmptyState
                title="No purchases"
                description="This player has not made any shop purchases yet."
                iconType="shop"
              />
            ) : (
              <Stack gap="xs" className="wp-stagger-children">
                {purchases.map((purchase: any) => (
                  <Card
                    key={purchase.id}
                    className={classes.purchaseRow}
                    p="sm"
                    radius="md"
                  >
                    <Group justify="space-between">
                      <Group gap="sm">
                        <ThemeIcon
                          size="md"
                          radius="md"
                          variant="light"
                          color="purple"
                        >
                          <IconShoppingCart size={16} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" fw={500}>
                            Product: {purchase.shopProductId}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {formatDate(purchase.createdAt)}
                          </Text>
                        </div>
                      </Group>
                      <Badge color="purple" variant="light">
                        x{purchase.quantity}
                      </Badge>
                    </Group>
                  </Card>
                ))}
              </Stack>
            )}
          </div>
        </Tabs.Panel>

        {/* Matches Tab */}
        <Tabs.Panel value="matches">
          <div className="wp-fade-in">
            <Group justify="space-between" mb="lg">
              <div>
                <Text fw={600} size="lg">
                  Match History
                </Text>
                <Text size="sm" c="dimmed">
                  {matchesCount} total matches
                </Text>
              </div>
            </Group>

            {matches.length === 0 ? (
              <EmptyState
                title="No matches"
                description="This player has not participated in any matches yet."
                iconType="game"
              />
            ) : (
              <Stack gap="xs" className="wp-stagger-children">
                {matches.map((match: any) => (
                  <Card
                    key={match.id}
                    className={classes.matchRow}
                    p="sm"
                    radius="md"
                  >
                    <Group justify="space-between">
                      <Group gap="sm">
                        <ThemeIcon
                          size="md"
                          radius="md"
                          variant="light"
                          color="cyan"
                        >
                          <IconDeviceGamepad2 size={16} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" fw={500}>
                            Match #{match.order ?? match.id}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {formatDate(match.createdAt)}
                          </Text>
                        </div>
                      </Group>
                      <Badge
                        color={
                          match.status === "COMPLETED"
                            ? "green"
                            : match.status === "IN_PROGRESS"
                              ? "amber"
                              : "dark"
                        }
                        variant="light"
                      >
                        {match.status}
                      </Badge>
                    </Group>
                  </Card>
                ))}
              </Stack>
            )}
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="custom-fields">
          <div className="wp-fade-in">
            <CustomFieldValuesPanel
              objectType="PlayerProfile"
              objectId={playerId}
            />
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="metadata">
          <div className="wp-fade-in">
            <ObjectMetadataPanel
              objectType="PlayerProfile"
              objectId={playerId}
            />
          </div>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};
